import React, { useEffect, useMemo, useState, useCallback } from "react";
import { apiUrls, getApiUrl, siteUrls } from "../config";
import { useNavigate } from "react-router-dom";
import { env, useGlobalState } from "../helpers/GlobalStateProvider";
import axios from "axios";
import { useModalState } from "../helpers/ModalProvider";
import { Button } from "../components/ui/button";
import { Box, Collapsible, Flex, IconButton, Link, MenuItem, MenuItemGroup, MenuTrigger, useDisclosure } from "@chakra-ui/react";
import { LuChevronDown, LuExternalLink, LuLogOut, LuMail, LuMenu, LuSettings, LuWallet } from "react-icons/lu";
import { MenuContent, MenuRoot, MenuSeparator } from "../components/ui/menu";
import { clearChatHistory } from "./chatbot/chatHistoryStorage";
import { getToken, clearToken } from "../helpers/tokenManager";

type HeaderContainerProps = {
  behavior?: "signin" | "dashboard" | "chatbot" | "docs" | "confirm_email" | "maintenance";
};

const HeaderContainer: React.FC<HeaderContainerProps> = ({ behavior }) => {
  const [hasBearerToken, setHasBearerToken] = useState(false);
  const navigate = useNavigate();
  const { info, refreshAccountInfo, isSignedIn, setBearerToken } = useGlobalState();
  const { onToggle, onClose, open } = useDisclosure();
  const { showErrorMessage } = useModalState();

  const accountTitle = info?.email ?? info?.walletAddress ?? "Account";

  const isSignin = behavior === "signin";
  const isDocsActive = behavior === "docs";
  const isConfirmEmail = behavior === "confirm_email";
  const isMaintenance = behavior === "maintenance";

  useEffect(() => {
    const token = getToken();
    setHasBearerToken(!!token);
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      const bearerToken = getToken();
      await axios.post(
        `${getApiUrl(env)}/v3/apiKey/logout`,
        {},
        { headers: { Authorization: `Bearer ${bearerToken}` } },
      );
    } catch (e) {
      // showErrorMessage("Failed to log out2. Please try again.");
    }
    await refreshAccountInfo();
    clearToken();
    setBearerToken(null);
    clearChatHistory();
    setHasBearerToken(false);
    navigate("/signin");
  }, [navigate, refreshAccountInfo, setBearerToken]);

  const signedIn = isSignedIn();

  const mainMenu = useMemo(() => {
    return (
      <>
        <Button
          variant="plain"
          asChild
          px="14px"
          size="sm"
          color="#27272a"
          fontSize="14px"
          fontWeight={500}
        >
          <Link
            href={siteUrls[env]}
            target="_blank"
            rel="noopener noreferrer"
            _focus={{ boxShadow: "none", outline: "none" }}
            _active={{ boxShadow: "none", outline: "none" }}
          >
            CoinCap <LuExternalLink />
          </Link>
        </Button>
        <Button
          px="14px"
          variant="plain"
          borderRadius="md"
          size="sm"
          color="#27272a"
          fontSize="14px"
          fontWeight={500}
          disabled={isConfirmEmail || isMaintenance}
          bg={behavior === "dashboard" ? "#f4f4f5" : undefined}
          asChild
        >
          <a
            href="/dashboard"
            onClick={(e) => {
              if (e.defaultPrevented) return;
              if (e.button !== 0) return;
              if (e.metaKey || e.altKey || e.ctrlKey || e.shiftKey) return;
              e.preventDefault();
              navigate("/dashboard");
              onClose();
            }}
          >
            Dashboard
          </a>
        </Button>
        <Button
          px="14px"
          variant="plain"
          borderRadius="md"
          size="sm"
          color="#27272a"
          fontSize="14px"
          fontWeight={500}
          disabled={isConfirmEmail || isMaintenance}
          bg={behavior === "chatbot" ? "#f4f4f5" : undefined}
          asChild
        >
          <a
            href="/chatbot"
            onClick={(e) => {
              if (e.defaultPrevented) return;
              if (e.button !== 0) return;
              if (e.metaKey || e.altKey || e.ctrlKey || e.shiftKey) return;
              e.preventDefault();
              navigate("/chatbot");
              onClose();
            }}
          >
            AI Chatbot
          </a>
        </Button>
        <Button
          px="14px"
          variant="plain"
          borderRadius="md"
          size="sm"
          color="#27272a"
          fontSize="14px"
          fontWeight={500}
          aria-current={isDocsActive && !isSignin ? "page" : undefined}
          bg={isDocsActive && !isSignin ? "#f4f4f5" : undefined}
          asChild
        >
          <a
            href="/api-docs"
            onClick={(e) => {
              if (e.defaultPrevented) return;
              if (e.button !== 0) return;
              if (e.metaKey || e.altKey || e.ctrlKey || e.shiftKey) return;
              e.preventDefault();
              navigate("/api-docs");
              onClose();
            }}
          >
            API Docs
          </a>
        </Button>
      </>
    );
  }, [behavior, isDocsActive, isSignin, isConfirmEmail, isMaintenance, navigate, onClose]);

  const userMenu = useMemo(() => {
    if (signedIn) {
      if (isConfirmEmail) {
        return (
          <Button variant="solid" colorPalette="purple" bg="#9333ea" size="sm" disabled>
            My Account
          </Button>
        );
      }

      return (
        <MenuRoot>
          <MenuTrigger disabled={!hasBearerToken}>
            <Button variant="solid" colorPalette="purple" bg="#9333ea" size="sm">
              My Account <LuChevronDown />
            </Button>
          </MenuTrigger>
          <MenuContent portalled={true}>
            <MenuItemGroup>
              <MenuItem value={accountTitle} alignSelf="stretch">
                <Flex width="193px" alignItems="center" gap={2} alignSelf="stretch" minWidth={0} overflow="hidden">
                  {accountTitle === info?.walletAddress ? <LuWallet style={{ flexShrink: 0 }} /> : <LuMail style={{ flexShrink: 0 }} />}
                  <Flex flex={1} overflow="hidden">
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        width: "100%",
                        display: "block",
                      }}
                    >
                      {accountTitle}
                    </span>
                  </Flex>
                </Flex>
              </MenuItem>
            </MenuItemGroup>
            <MenuSeparator />
            <MenuItemGroup>
              <MenuItem value="settings" asChild>
                <a href="/settings">
                  <LuSettings />
                  Settings
                </a>
              </MenuItem>
              <MenuItem value="logout" color="fg.error" onClick={handleLogout}>
                <LuLogOut /> Log Out
              </MenuItem>
            </MenuItemGroup>
          </MenuContent>
        </MenuRoot>
      );
    }

    // On /signin (and the nav-locked confirm-email / maintenance states) the
    // "Sign In" CTA has nowhere useful to go. Render it as a muted current-page
    // pill rather than a faded, broken-looking disabled purple button.
    if (isSignin || isConfirmEmail || isMaintenance) {
      return (
        <Button
          variant="ghost"
          size="sm"
          bg="#f4f4f5"
          color="#52525b"
          cursor="default"
          _hover={{ bg: "#f4f4f5" }}
          aria-current={isSignin ? "page" : undefined}
        >
          Sign In
        </Button>
      );
    }

    return (
      <Button variant="solid" colorPalette="purple" bg="#9333ea" size="sm" asChild>
        <a href="/signin">Sign In</a>
      </Button>
    );
  }, [accountTitle, handleLogout, hasBearerToken, info?.walletAddress, signedIn, isSignin, isConfirmEmail, isMaintenance]);

  return (
    <Box borderTopRadius={0} zIndex={1000} bg={open ? "bg.surface" : "transparent"}>
      <Flex px="36px" width="full" py={4} justifyContent="space-between">
        <IconButton variant="outline" display={{ base: "flex", md: "none" }} onClick={onToggle} aria-label="Toggle navigation menu">
          <LuMenu />
        </IconButton>
        <Box flexShrink={0} width="85px" height="42px">
          <img
            src="/external/Plum.svg"
            alt="Logo"
            style={{
              display: "block",
              width: "100%",
              height: "100%",
              objectFit: "contain",
            }}
          />
        </Box>

        <Flex gap={8} ml="auto" display={{ base: "none", md: "flex" }}>
          {mainMenu}
          {userMenu}
        </Flex>

        <Flex display={{ base: "flex", md: "none" }}>{userMenu}</Flex>
      </Flex>

      <Collapsible.Root open={open} lazyMount>
        <Collapsible.Content px={4}>
          <Flex flexDir="column" gap={2} justifyContent="flex-start" alignItems="flex-start" pb={4}>
            {mainMenu}
          </Flex>
        </Collapsible.Content>
      </Collapsible.Root>
    </Box>
  );
};

export default HeaderContainer;
